import * as qs from 'qs';
import _ from 'lodash';
import type Passport from '@patsnap-utils/passport/src/passport';
import { needAddTrialApi } from './apis';
import type { HeaderService } from '@patsnap-utils/biz';
import type { AxiosError } from 'axios';
import { addTrialDeferred } from '../account';
import { initCookie } from '../init-cookie';

export interface IAddTrialExtendBody {
    /**
     * 账号注册来源
     */
    channel_source: string
    /**
     * 活动类型
     */
    activity_type: string
    /**
     * 邀请人id
     */
    inviter_id: string
    /**
     * 企业邀请code 企业邀请注册时有值
     */
    invitation_code: string
    /**
     * 邀请人邀请码
     */
    media_type: 'Mobile' | ''
    /**
     * 邀请注册来源
     */
    from?: string
}

const MAX_TRIAL_STORAGE_KEY = 'ADDTRIAL_clickcount';
const MAX_TRIAL_STORAGE_COUNT = 5;
const DRAFTING_PRODUCT_SOURCE = 'eureka_ip_drafting';
const NO_PERMISSION_ADD_TRIAL_ERROR_CODE = 90112055;
const ACCOUNT_STATUS_PATH = '/account-status';
const ONBOARDING_PATH = '/onboarding';

enum NeedAddTrialActionType {
    DIRECT_LOGIN = 'direct_login',
    SWITCH_TO_INDIVIDUAL_TENANT = 'switch_to_individual_tenant',
    SWITCH_AND_GRANT_PRIVILEGE = 'switch_and_grant_privilege',
    GRANT_INDIVIDUAL_TENANT_PRIVILEGE = 'grant_individual_tenant_privilege',
    CREATE_INDIVIDUAL_TENANT = 'create_individual_tenant',
    CONTACT_ADMIN = 'contact_admin',
    NEW_USER_ONBOARDING = 'new_user_onboarding',
}

function isAxiosError(error: any): error is AxiosError {
    if (!error) { return false; }
    return error.isAxiosError;
}

function getNeedAddTrialErrorCode(data: unknown) {
    return _.get(data, 'error_code')
        ?? _.get(data, 'data.error_code')
        ?? _.get(data, 'response.data.error_code')
        ?? _.get(data, 'cause.error_code');
}

function isNoPermissionAddTrialError(data: unknown) {
    return getNeedAddTrialErrorCode(data) === NO_PERMISSION_ADD_TRIAL_ERROR_CODE;
}

function isAccountStatusPage() {
    if (typeof window === 'undefined') { return false; }
    return window.location.pathname.includes(ACCOUNT_STATUS_PATH)
        || window.location.hash.startsWith(`#${ACCOUNT_STATUS_PATH}`);
}

function isOnboardingPage() {
    if (typeof window === 'undefined') { return false; }
    return window.location.pathname.startsWith(ONBOARDING_PATH);
}

function getEurekaUri() {
    const eurekaUri = import.meta.env.VITE_EUREKA_URI || window.location.origin;
    return eurekaUri.replace(/\/$/, '');
}

function redirectToNoPermissionPage() {
    if (typeof window === 'undefined') { return; }
    window.location.href = `${getEurekaUri()}/403/`;
}

function redirectToAccountStatusPage() {
    if (typeof window === 'undefined') { return; }
    const params = new URLSearchParams({
        account_status_from: window.location.href,
    });
    window.location.href = `${getEurekaUri()}${ACCOUNT_STATUS_PATH}?${params.toString()}`;
}

function redirectToOnboardingPage() {
    if (typeof window === 'undefined') { return; }
    const params = new URLSearchParams({
        next: window.location.href,
    });
    window.location.href = `${getEurekaUri()}${ONBOARDING_PATH}/?${params.toString()}`;
}

class AddTrialService {
    passport: Passport | void = undefined;

    ENABLE_AUTO_ADD_TRIAL = true;
    async addTrial(headerService: HeaderService, passport: Passport | void) {
        try {
            this.passport = passport;
            if (isAccountStatusPage()) {
                return;
            }
            if (isOnboardingPage()) {
                return;
            }
            const isCompanyAccount = this.isCompanyAccount(headerService);
            const grantedProductIds: string[] | undefined = headerService.account?._info?.granted_product_ids;
            if (!isCompanyAccount && grantedProductIds === undefined) {
                return;
            }
            const extendBody = this.getAddTrialExtendBody();

            const needAddTrialData = await needAddTrialApi(extendBody);
            if (isNoPermissionAddTrialError(needAddTrialData)) {
                redirectToNoPermissionPage();
                return;
            }

            const actionType = (_.get(needAddTrialData, 'data.action_type', '')
                || _.get(needAddTrialData, 'data.actionType', '')) as NeedAddTrialActionType | string;
            this.ENABLE_AUTO_ADD_TRIAL = false;

            switch (actionType) {
                case NeedAddTrialActionType.DIRECT_LOGIN:
                    break;
                case NeedAddTrialActionType.GRANT_INDIVIDUAL_TENANT_PRIVILEGE: {
                    const trialCounts = Number(sessionStorage.getItem(MAX_TRIAL_STORAGE_KEY) || 0);
                    if (trialCounts >= MAX_TRIAL_STORAGE_COUNT) {
                        sessionStorage.removeItem(MAX_TRIAL_STORAGE_KEY);
                        throw new Error(`Trial counts is over ${MAX_TRIAL_STORAGE_COUNT}!`);
                    }
                    sessionStorage.setItem(MAX_TRIAL_STORAGE_KEY, String(trialCounts + 1));
                    localStorage.removeItem('patsnap_header_account_info');
                    console.warn('[AddTrial]', '赋权成功，刷新页面生效');
                    this.passport?.removeLocal();
                    window.location.reload();
                    break;
                }
                case NeedAddTrialActionType.NEW_USER_ONBOARDING:
                    redirectToOnboardingPage();
                    break;
                case NeedAddTrialActionType.CONTACT_ADMIN:
                case NeedAddTrialActionType.SWITCH_TO_INDIVIDUAL_TENANT:
                case NeedAddTrialActionType.SWITCH_AND_GRANT_PRIVILEGE:
                case NeedAddTrialActionType.CREATE_INDIVIDUAL_TENANT:
                default:
                    redirectToAccountStatusPage();
                    break;
            }
        } catch (error) {
            if (isNoPermissionAddTrialError(error)) {
                redirectToNoPermissionPage();
                return;
            }
            console.error('[AddTrial]', error);
        } finally {
            this.ENABLE_AUTO_ADD_TRIAL = false;
            addTrialDeferred.resolve(true);
        }
    }

    isCompanyAccount(headerService: HeaderService) {
        const account = headerService.account as any;
        return !!(
            account?.company_id
            || account?._info?.company_id
            || account?._info_?.company_id
        );
    }

    isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
        );
    }

    getSource() {
        const hash = window.location.hash;
        const queryString = hash.includes('?') ? '?' + hash.split('?')[1] : window.location.search;
        const searchParams = new URLSearchParams(queryString);
        const from = searchParams.get('from') || searchParams.get('start_from');
        if (from && (from.startsWith('eureka-seo-') || from.startsWith('website-seo-'))) {
            return from;
        }
        return initCookie.getCampaignName() || 'organic';
    }

    getAddTrialExtendBody(): IAddTrialExtendBody {
        const hash = window.location.hash;
        const queryString = hash.includes('?') ? '?' + hash.split('?')[1] : window.location.search;

        const query = qs.parse(queryString, {
            ignoreQueryPrefix: true,
        });

        const product_source = DRAFTING_PRODUCT_SOURCE;
        const entry_channel = initCookie.getEntryChannel();
        const source = initCookie.getCampaignName() || 'organic';
        const visit_id = initCookie.getVisitorId();

        return {
            from: this.getSource(),
            ...query,
            media_type: this.isMobileDevice() ? 'Mobile' : '',
            product_source,
            entry_channel,
            source,
            visit_id,
        };
    }

    getEmpowerType(from?: string) {
        const fromLifeScienceCheckList = ['synapse', 'chemical', 'bio', 'hiro_ls', 'ls'];
        if (fromLifeScienceCheckList.includes(from || '')) return 'LS';
        if (from === 'ls_plg') return 'LS_PLG';
        if (from === 'discovery_seo_company') return 'INSIGHTS';
        if (from?.includes('eureka-seo-material')) return 'MATERIAL';
        if (from === 'feishu') return 'FEISHU';
        return 'AIR';
    }
}

export const addTrialService = new AddTrialService();

export function errorHandleIgnore403(e: Error) {
    return isAxiosError(e) && e.response?.status === 403;
}
